import { useAppDispatch, useAppSelector } from '@hooks/app/redux';
import useXmppContacts from '@hooks/query/xmpp/useXmppContacts';
import getFullNameFromProfile from '@lib/utils/names/getFullNameFromProfile';
import { actions as chatActions } from '@store/ducks/direct-chat';
import React from 'react';
import { IClientSearchItem } from 'types/client.types';
import { TLastMessage } from 'types/direct-chat';

export type TInboxItem = {
  fullName: string;
  messagePreview: string;
  patient: IClientSearchItem;
  unreadsCount: number;
};

function lastMessageComparator(a: TLastMessage, b: TLastMessage) {
  // @ts-expect-error: known date comparison
  return new Date(a.stamp) - new Date(b.stamp);
}

function useLastMessages() {
  const dispatch = useAppDispatch();
  const unreads = useAppSelector((state) => state.directChat.unreads);
  const xmppMessages = useAppSelector((state) => state.directChat.lastMessages);
  const isLoading = useAppSelector((state) => state.directChat.isLoadingLastMessages);
  const xmppContacts = useXmppContacts(xmppMessages.map((m) => m.from));

  React.useEffect(() => {
    dispatch(chatActions.getLastMessagesRequest());
    dispatch(chatActions.getUnreadsRequest());
  }, []);

  const { inbox, unreadCount } = React.useMemo(() => {
    const inboxMessages: TInboxItem[] = [];
    let unreadCount = 0;

    if (!xmppMessages || !xmppContacts.data) {
      return { inbox: inboxMessages, unreadCount: unreadCount };
    }

    const lastMessages = [...xmppMessages];
    lastMessages.sort(lastMessageComparator);

    for (const lastMessage of lastMessages) {
      const patient = xmppContacts.data.find((p) => p.jabberId === lastMessage.from);

      if (!patient) {
        continue;
      }

      let message = lastMessage.body;

      if (lastMessage.attachmentName && message && message !== 'attachment') {
        message = `${message} (${lastMessage.attachmentName})`;
      } else if (lastMessage.attachmentName) {
        message = lastMessage.attachmentName;
      }

      const personUnread = unreads[lastMessage.from] || 0;
      unreadCount += personUnread ?? 0;

      inboxMessages.push({
        fullName: getFullNameFromProfile(patient?.profile),
        messagePreview: message,
        patient,
        unreadsCount: personUnread,
      });
    }

    return { inbox: inboxMessages, unreadCount };
  }, [xmppContacts.data, xmppMessages, unreads]);

  return { inbox, isLoading, unreadCount };
}

export default useLastMessages;
